import { UserPermissions } from "../../../../model/members/UserPermissions";
import { AdminPermissions } from "../../../../model/auth/Permissions";

// Config-driven mode matrix — one component set serves all three audiences.
// Generalises the old `selfServiceMode` boolean. Each flag decides what is
// visible and what is editable. A `false` "edit*" flag renders the field
// disabled (read-only); a `false` panel flag omits the panel/tab entirely.
// Mirrors the prototype's window.Modes (design_handoff_member_profile_v2).
export interface MemberProfileMode {
    checkInButton: boolean;   // show check-in CTA
    actions: boolean;         // show the actions rail: generate card / take payment / activate (cancel gated separately by cancelMembership)
    cancelMembership: boolean; // enable the cancel-membership buttons within actions
    notes: boolean;           // Staff Notes card (ADMIN/STAFF ONLY)
    // Edit/add/delete the direct-debit price override from the membership-category
    // area (Admin + Front-of-House). The amber marker itself is visible to
    // everyone incl. self-service — this flag only gates the edit controls.
    editDirectDebitOverride: boolean;
    // Staff-only generic Metadata section (view + add/edit/remove). Hidden in
    // self-service. True for Admin and Front-of-House.
    metadata: boolean;
    editName: boolean;
    editEmail: boolean;
    editMembership: boolean;  // category (+ valid-from/to, unless editValidityDates overrides)
    // Valid-from / valid-to editability. Falls back to editMembership when
    // undefined, so existing modes are unchanged; onboarding sets it false to
    // unlock the category while keeping the server-owned dates locked.
    editValidityDates?: boolean;
    editContact: boolean;     // phone, address, DoB, emergency contact
    photoUpload: boolean;
    checkins: boolean;
    cancellations: boolean;       // show the cancellations list
    cancellationsEdit: boolean;   // allow edit / mark-inactive on cancellations
    ddMandate: boolean;
    ddMandateEdit: boolean;
    recurring: boolean;
    joiningFee: boolean;
    payments: boolean;
    pos: boolean;        // show the POS transactions tab
    posEdit: boolean;    // allow editing a POS transaction
    health: boolean;
    // Allow recording a health review (who/when + notes). Staff only — a member
    // viewing their own profile sees reviews read-only and can never edit them.
    healthReview: boolean;
}

const adminMode: MemberProfileMode = {
    checkInButton: true,
    actions: true,
    cancelMembership: true,
    notes: true,
    editDirectDebitOverride: true,
    metadata: true,
    editName: true,
    editEmail: true,
    editMembership: true,
    editContact: true,
    photoUpload: true,
    checkins: true,
    cancellations: true,
    cancellationsEdit: true,
    ddMandate: true,
    ddMandateEdit: true,
    recurring: true,
    joiningFee: true,
    payments: true,
    pos: true,
    posEdit: true,
    health: true,
    healthReview: true,
};

// Front-of-house: full member-management view — check in, edit membership &
// contact details, manage recurring items and joint membership, see payments,
// activity and cancellations. Cancellations are view-only, the membership-cancel
// actions (Schedule Cancellation / Cancel Immediately) are disabled, and the DD
// mandate is read-only; everything else mirrors admin.
const frontdeskMode: MemberProfileMode = {
    ...adminMode,
    cancelMembership: false,   // can take payment / activate, but not cancel a membership
    ddMandateEdit: false,      // DD mandate read-only
    cancellationsEdit: false,  // can see cancellations but not edit / reverse them
};

// The member viewing their own profile. Staff notes never render here. They can
// see their POS transactions but not edit them. They CAN see and submit/update
// their own health questionnaire and view its history, but reviews are staff-only
// (healthReview:false) — they see review notes read-only.
const selfserviceMode: MemberProfileMode = {
    ...adminMode,
    checkInButton: false,
    actions: false,
    cancelMembership: false,
    notes: false,
    // The DD-override marker still renders read-only in self-service; the member
    // can never edit it, and the generic Metadata section is hidden entirely.
    editDirectDebitOverride: false,
    metadata: false,
    editName: false,
    editEmail: false,
    editMembership: false,
    photoUpload: false,
    checkins: false,
    cancellations: false,
    cancellationsEdit: false,
    ddMandateEdit: false,
    recurring: false,
    joiningFee: false,
    pos: true,
    posEdit: false,
    health: true,
    healthReview: false,
};

// Self-service create (onboarding): the new member fills in their own details.
// Built on selfservice but unlocks name + membership category so they can pick a
// plan; email stays server-owned (from Auth0) and the validity dates stay locked
// (the server sets validFrom and derives validTo). No photo upload until the
// member row exists.
const onboardingMode: MemberProfileMode = {
    ...selfserviceMode,
    editName: true,
    editEmail: false,
    editMembership: true,
    editValidityDates: false,
    photoUpload: false,
    // No health tab during the blank create — the member has no membershipId yet.
    // The questionnaire is collected by the mandatory pre-save prompt and sent
    // with the create request instead.
    health: false,
};

// Admin/front-of-house blank create: full admin edit surface, but no photo
// upload until the member exists, and validity dates are explicitly editable.
const adminCreateMode: MemberProfileMode = {
    ...adminMode,
    photoUpload: false,
    editValidityDates: true,
};

export const MEMBER_PROFILE_MODES = {
    admin: adminMode,
    frontdesk: frontdeskMode,
    selfservice: selfserviceMode,
    onboarding: onboardingMode,
    adminCreate: adminCreateMode,
} as const;

export type MemberProfileModeKey = keyof typeof MEMBER_PROFILE_MODES;

const has = (permissions: UserPermissions | undefined, name: string): boolean =>
    Array.isArray(permissions?.permissions) &&
    permissions!.permissions.some((p) => p.name === name);

// Which create/onboarding flow (if any) the screen is entering in.
export type ProfileEntryMode = 'view' | 'onboarding' | 'adminCreate';

// Derive the mode once from the existing selfServiceMode flag + the current
// user's role + the entry mode. Club/Fortius admins get the full admin view;
// front-of-house get the reduced front-desk view; members on /my-profile get
// self-service. The onboarding / adminCreate entry modes unlock the fields
// needed to create a brand-new member.
export function deriveMemberProfileMode(
    selfServiceMode: boolean,
    permissions: UserPermissions | undefined,
    entry: ProfileEntryMode = 'view',
): MemberProfileMode {
    if (entry === 'onboarding') return MEMBER_PROFILE_MODES.onboarding;
    if (entry === 'adminCreate') return MEMBER_PROFILE_MODES.adminCreate;
    if (selfServiceMode) return MEMBER_PROFILE_MODES.selfservice;
    if (has(permissions, AdminPermissions.ClubAdmin) || has(permissions, AdminPermissions.FortiusAdmin)) {
        return MEMBER_PROFILE_MODES.admin;
    }
    if (has(permissions, AdminPermissions.FrontOfHouse)) {
        return MEMBER_PROFILE_MODES.frontdesk;
    }
    // The screen's route is already permission-gated to staff; default to the
    // most restrictive staff view if a specific role can't be determined.
    return MEMBER_PROFILE_MODES.frontdesk;
}
